import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Opportunity } from '../types/api';
import opportunityService from '../services/opportunityService';
import NewOpportunityModal from '../components/NewOpportunityModal';
import PermissionGuard from '../components/PermissionGuard';
import AccessDenied from '../components/AccessDenied';

/**
 * OpportunitiesPage
 *
 * Lists PBS-local opportunities - prospective work that does not exist in Redmine.
 * From here a project owner can create one, pick one to analyse in What-If, or
 * remove one that is no longer needed.
 */
const OpportunitiesPage: React.FC = () => {
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showNewModal, setShowNewModal] = useState<boolean>(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const loadOpportunities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await opportunityService.listOpportunities();
      setOpportunities(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load opportunities');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOpportunities();
  }, [loadOpportunities]);

  // Hand off to What-If with this opportunity preselected.
  const handleAnalyze = (opportunity: Opportunity) => {
    navigate(`/whatif?opportunity=${opportunity.id}`);
  };

  const handleCreated = (created: Opportunity) => {
    setShowNewModal(false);
    // Straight into the analysis for the opportunity just created.
    navigate(`/whatif?opportunity=${created.id}&addMembers=1`);
  };

  const handleDelete = async (opportunity: Opportunity) => {
    const confirmed = window.confirm(
      `Delete "${opportunity.name}"?\n\nIt will be removed from the opportunity list. Any saved scenario data is kept.`
    );
    if (!confirmed) return;

    setDeletingId(opportunity.id);
    try {
      await opportunityService.deleteOpportunity(opportunity.id);
      await loadOpportunities();
    } catch (err: any) {
      setError(err?.message || 'Failed to delete opportunity');
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = opportunities.filter((o) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    return (
      o.name.toLowerCase().includes(term) ||
      (o.description || '').toLowerCase().includes(term)
    );
  });

  const formatDate = (value?: string | null) => {
    if (!value) return '—';
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString();
  };

  return (
    <PermissionGuard
      pbsRole="project_owner"
      fallback={<AccessDenied message="Only project owners can manage opportunities." />}
    >
      <div className="space-y-6">
        {/* Page Header */}
        <div className="border-b border-gray-200 pb-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Opportunities</h2>
              <p className="mt-1 text-sm text-gray-600">
                Prospective work not yet in Redmine. Create one to model its team and costs.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowNewModal(true)}
              className="inline-flex items-center px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 transition-all"
            >
              <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Opportunity
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md" role="alert" aria-live="polite">
            <p className="text-sm text-red-800 font-medium">{error}</p>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Search */}
          <div className="p-4 border-b border-gray-200">
            <label htmlFor="opportunity-search" className="sr-only">
              Search opportunities
            </label>
            <input
              id="opportunity-search"
              type="text"
              placeholder="Search opportunities by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
            />
          </div>

          {/* List */}
          {loading ? (
            <div className="p-8 text-center" role="status" aria-live="polite">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto" aria-hidden="true"></div>
              <p className="mt-3 text-sm text-gray-600">Loading opportunities...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-3 text-sm font-medium text-gray-900">
                {opportunities.length === 0 ? 'No opportunities yet' : 'No matching opportunities'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {opportunities.length === 0
                  ? 'Create one to start modelling a prospective project.'
                  : 'Try a different search term.'}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {filtered.map((opportunity) => (
                <li key={opportunity.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-sm font-medium text-gray-900 truncate">{opportunity.name}</h3>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 whitespace-nowrap">
                          Opportunity
                        </span>
                      </div>
                      {opportunity.description && (
                        <p className="mt-1 text-xs text-gray-600 line-clamp-2">{opportunity.description}</p>
                      )}
                      <div className="mt-1.5 flex items-center space-x-4 text-xs text-gray-500">
                        <span>ID: {opportunity.id}</span>
                        <span>Created: {formatDate(opportunity.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => handleAnalyze(opportunity)}
                        className="px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 transition-all"
                      >
                        Analyze
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(opportunity)}
                        disabled={deletingId === opportunity.id}
                        className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1 disabled:opacity-50 transition-all"
                      >
                        {deletingId === opportunity.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <NewOpportunityModal
          isOpen={showNewModal}
          onClose={() => setShowNewModal(false)}
          onCreated={handleCreated}
        />
      </div>
    </PermissionGuard>
  );
};

export default OpportunitiesPage;
